// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, ListItem, ListItemText, MultiSelect} from '@deere/isg.component-library';
import {MultiselectFilterList} from '@deere/isg.component-library/MultiselectFilterList';
import Stack from '@mui/material/Stack';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import SidebarHeader from 'OnLabor/workboard/calendar/sidebar-header';
import {openDialog} from 'Store/actions/dialogs';
import {getTodos} from 'Services/membership-service';
import {createTodo, updateTodo} from 'Services/todo-service';
import {fetchEffectData, useDeepMemo} from 'Utils/react-utils';
import {DATE_FORMATS, formatTime} from 'Utils/time-utils';
import {transformAndSortTodos} from 'Utils/workboard-utils';
import dialogTypes from 'Ui/components/common/dialog-types';
import {clsx} from 'clsx';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {MANAGE_WORKBOARDS} from 'Common/constants/business-activities';
import {PRO_GOLF_GROUP_SMS} from 'Common/constants/isg-toggles';
import {fetchAndFormatOperator} from '../utils/fetch-operators';

async function saveTodo({
    appUserId,
    completed,
    membershipId,
    fetchTodos,
    todoItem
}) {
    const todoData = {
        completed,
        dateSelect: formatTime(new Date(), DATE_FORMATS.day),
        membershipId,
        title: todoItem?.title
    };

    if (completed) {
        await updateTodo(todoData, todoItem.id);
    } else {
        todoData.appUserId = appUserId;

        await createTodo(todoData);
    }

    await fetchTodos();
}

function getQuickTodoList(
    appUserId,
    fetchTodos,
    isgToggles,
    membershipId,
    todoTemplates,
    translations
) {
    return (
        <MultiSelect
            className={'todo_selectInput'}
            items={todoTemplates}
            labels={{
                placeholder: isgToggles[PRO_GOLF_GROUP_SMS] ? 'Select To-Do\'s' : translations.ONLINK_QUICK_TODO
            }}
            noSearch={true}
            onChange={([id]) => saveTodo({
                appUserId,
                completed: false,
                membershipId,
                fetchTodos,
                todoItem: todoTemplates?.find((todoTemplate) => todoTemplate.id === id)
            })}
            selectedIds={[]}
            single={true}
        />
    );
}

function getTodosList(
    appUserId,
    isgToggles,
    isMobile,
    fetchTodos,
    membershipId,
    todosLoading,
    todos,
    canEditTodo
) {
    const classNameValue = clsx('multi-select-filter-list', {
        'mobile-group': isMobile
    });
    const heightValue = isMobile ? '200px' : '100%';

    return isgToggles[PRO_GOLF_GROUP_SMS] ? (
        <LoadingWrapper
            className='table-loading-icon'
            loading={todosLoading}
            size='50px'
        >
            <Stack
                className={classNameValue}
                height={heightValue}
                marginTop='8px'
                minHeight={52}
            >
                {canEditTodo ?
                    <MultiselectFilterList
                        autoFocus={true}
                        disableSorting={true}
                        items={todos}
                        noSearch={true}
                        onChange={([id]) => saveTodo({
                            appUserId,
                            completed: true,
                            membershipId,
                            fetchTodos,
                            todoItem: todos?.find((todo) => todo.id === id)
                        })}
                        selectedIds={[]}
                        single={true}
                    /> : todos.map((todo) => (
                        <ListItem key={todo.id}>
                            <ListItemText
                                primary={todo.title}
                                secondary={todo.subtitle}
                            />
                        </ListItem>))
                }
            </Stack>
        </LoadingWrapper>
    ) : (
        <LoadingWrapper
            className='table-loading-icon'
            loading={todosLoading}
            size='50px'
        >
            <Stack
                className={classNameValue}
                height={heightValue}
                marginTop='8px'
                minHeight={52}
            >
                {canEditTodo ?
                    <MultiselectFilterList
                        autoFocus={true}
                        disableSorting={true}
                        items={todos}
                        noSearch={true}
                        onChange={([id]) => saveTodo({
                            appUserId,
                            completed: true,
                            membershipId,
                            fetchTodos,
                            todoItem: todos?.find((todo) => todo.id === id)
                        })}
                        selectedIds={[]}
                        single={true}
                    /> : todos.map((todo) => (
                        <ListItem key={todo.id}>
                            <ListItemText
                                primary={todo.title}
                                secondary={todo.subtitle}
                            />
                        </ListItem>))
                }
            </Stack>
        </LoadingWrapper>
    );
}

function TodoList(props) {
    const {
        appUserId,
        isgToggles,
        isMobile,
        membershipId,
        orgId,
        openManageTodoDialog,
        todoTemplates,
        translations
    } = props;

    const todosMasterList = React.useRef([]);
    const [todos, setTodos] = React.useState([]);
    const [todosLoading, setTodosLoading] = React.useState(false);
    const [assignees, setAssignees] = React.useState([]);
    const [teamMembersMap, setTeamMembersMap] = React.useState(new Map());

    const canEditTodo = isAuthorized({
        myJdPermissions: MANAGE_WORKBOARDS
    }, props);

    const fetchTodos = React.useCallback(async (isMounted = () => true) => {
        setTodosLoading(true);

        const {todos: todosList} = await getTodos();

        if (isMounted()) {
            todosMasterList.current = todosList;
            setTodos(transformAndSortTodos(todosList, true, teamMembersMap));
            setTodosLoading(false);
        }
    }, [teamMembersMap]);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        await fetchAndFormatOperator({
            isMounted,
            setAssignees,
            setTeamMembersMap,
            orgId
        });
        await fetchTodos(isMounted);
    }), [membershipId]);

    const manageAccordionTodoProps = useDeepMemo(() => {
        return {
            children: 'Manage',
            disabled: todosLoading,
            onClick() {
                openManageTodoDialog({
                    fetchTodos,
                    translations,
                    todos: todosMasterList,
                    assignees,
                    teamMembersMap
                });
            }
        };
    }, [fetchTodos, todosLoading, todosMasterList.current, translations]);

    const manageTodoProps = useDeepMemo(() => {
        const sharedProps = {
            children: translations.ONLINK_MANAGE_TODOS,
            disabled: todosLoading,
            onClick() {
                openManageTodoDialog({
                    fetchTodos,
                    translations,
                    todos: todosMasterList,
                    assignees,
                    teamMembersMap
                });
            }
        };

        return isMobile ? {
            ...sharedProps,
            className: 'mobile-group',
            sx: {
                marginBottom: '10px',
                width: 'fit-content'
            }
        } : sharedProps;
    }, [fetchTodos, isMobile, todosLoading, todosMasterList.current, translations]);

    const quickTodoList = getQuickTodoList(appUserId, fetchTodos, isgToggles, membershipId, todoTemplates, translations);

    const AccordionVariant = () => (
        <Accordion className='workboard_accordion'>
            <AccordionSummary>
                {translations.ONLINK_TODOS_TITLE}
            </AccordionSummary>
            <AccordionDetails className='workboard_accordionContent'>
                <Box>
                    {canEditTodo && quickTodoList}
                    {getTodosList(appUserId, isgToggles, false, fetchTodos, membershipId, todosLoading, todos, canEditTodo)}
                    <Box
                        sx={
                            {
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center'
                            }
                        }
                    >
                        <Button
                            {...manageAccordionTodoProps}
                            variant='text'
                        />
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );

    if (isgToggles[PRO_GOLF_GROUP_SMS]) {
        return AccordionVariant();
    }

    return isMobile ? (
        <>
            <SidebarHeader/>
            <Accordion
                className='todos-accordion'
                classes={{
                    root: 'accordion-root'
                }}
                elevation={0}
                headerTitle={translations.ONLINK_TODOS}
                style={{
                    border: 'none',
                    borderRadius: '4px',
                    boxShadow: 'none'
                }}
            >
                <div className='todos-accordion-child'>
                    <Button
                        {...manageTodoProps}
                        variant='secondary'
                    />
                    {canEditTodo && quickTodoList}
                    {getTodosList(appUserId, isgToggles, true, fetchTodos, membershipId, todosLoading, todos, canEditTodo)}
                </div>
            </Accordion>
        </>
    ) : (
        <>
            <SidebarHeader title={translations.ONLINK_TODOS}>
                <Button
                    {...manageTodoProps}
                    variant='secondary'
                />
            </SidebarHeader>
            {canEditTodo && quickTodoList}
            {getTodosList(appUserId, isgToggles, false, fetchTodos, membershipId, todosLoading, todos, canEditTodo)}
        </>
    );
}

TodoList.propTypes = {
    appUserId: PropTypes.string,
    isgToggles: PropTypes.object,
    isMigrated: PropTypes.bool,
    isMobile: PropTypes.bool,
    membershipId: PropTypes.string,
    myJdPermissions: PropTypes.myJdPermissions,
    openManageTodoDialog: PropTypes.func,
    orgId: PropTypes.number,
    todoTemplates: PropTypes.arrayOf(PropTypes.object),
    translations: PropTypes.translations
};

export function mapDispatchToProps(dispatch) {
    return {
        openManageTodoDialog(props) {
            dispatch(openDialog(dialogTypes.MANAGE_TODO_DIALOG, props));
        }
    };
}

export function mapStateToProps(state) {
    return {
        isgToggles: state.account.isgToggles,
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions,
        orgId: state.membership.orgId
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MediaQuery(MOBILE_MEDIA_QUERY)(TodoList));
